import React, {useState, useEffect} from 'react';
import {Container} from 'react-bootstrap';
import moment from 'moment';
import {FormattedMessage, useIntl} from 'react-intl';
import '../styles/company.css';
import '../styles/typography.css';
import {CustomButton} from '../components/CustomButton';
import {ReactComponent as CloseIcon} from '../images/close.svg';
import NewInfoRow from '../components/NewInfoRow';
import webClient from '../api/webClient';

const NewCompany = ({companyId, closeCompanyInfo}) => {
  const [yritys, setYritys] = useState(null);
  const intl = useIntl();

  const fetchCompanyInfo = async id => {
    try {
      await webClient.post('Yrityksenperustiedot/Hae', {yritysid: id}).then(response => {
        setYritys(response.data);
      });
    } catch (error) {
      /* eslint-disable no-console */
      console.error('Failed to fetch company', error);
    }
  };

  // componentDidMount equivalent
  useEffect(() => {
    fetchCompanyInfo(companyId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // empty dependency → runs once after mount

  // componentDidUpdate equivalent
  useEffect(() => {
    if (companyId) {
      fetchCompanyInfo(companyId);
    }
  }, [companyId]); // runs every time companyId changes

  if (!yritys) return null;

  const recordOfficeEmail = intl.formatMessage({id: 'recordOfficeEmail'});
  const companyChangeFormUrl = intl.formatMessage({id: 'company-change-form-url'});
  const contactEmail = intl.formatMessage({id: 'contact-email'});

  const renderForceOfLaw = value => {
    if(value === true) {
        return intl.formatMessage({
          id: 'sanctions-force_of_law_true'
        }).toLowerCase();
      }
      else {
        return intl.formatMessage({
          id: 'sanctions-force_of_law_false'
        }).toLowerCase();
      }
  };

  const handlePositions = (asemat, locale) => {
    const localizedPositions = asemat.map(asema => (locale === 'fi' ? asema.AsemaFi : asema.AsemaSv));
    return localizedPositions.join(', ');
  };

  const sanctionDetailArray = yritys.Sanktiot.All.flatMap(item => [
    `${intl.locale === 'fi' ? item.TypeLabelFi ?? '' : item.TypeLabelSv ?? ''}`,
    `${intl.formatMessage({id: 'sanctions-docket'})} ${item.DiaariNumber ?? ''}`,
    `${intl.formatMessage({id: 'sanctions-verdict_date_new'})} ${moment(item.DecisionDate).format('DD.MM.YYYY')}`,
    `${intl.formatMessage({id: 'sanctions-force_of_law'})} ${item.Legal !== null ? renderForceOfLaw(item.Legal) : item.Legality}`,
    ''
  ]);

  const officesDetailArray = yritys.Toimipaikat.flatMap(item => [
    `${item.Katuosoite}, ${item.Postinumero} ${item.Postitoimipaikka}`
  ]);

  const responsibleDetailArray = yritys.VastaavaHenkilo.flatMap(item => [
    `${item.Nimi}`
  ]);

  const legalPersonDetailArray = yritys.Oikeushenkilot.flatMap(item => [
    `${item.Nimi}, ${handlePositions(item.Asemat, intl.locale)}`
  ]);

  return (
    <>
      {/* Overlay that covers the background when modal is open */}
      <div className="overlay d-none d-md-block visible"></div>

      <section
        className="company-info-container-wrapper"
        style={{backgroundColor: '#EAECE2', zIndex: 999}}
      >
        <Container className="company-info-container_rows-lvv">
          <CustomButton className="button close" autoFocus onClick={closeCompanyInfo}>
            <CloseIcon />
            <FormattedMessage id="company-close_info" defaultMessage="Sulje lisätiedot" />
          </CustomButton>
          <h1 className="h1-bold">{yritys.Toiminimi}</h1>
          <NewInfoRow labelId="company-company_aux_name" arvo={yritys.Aputoiminimi} />
          <NewInfoRow labelId="company-company_businessID" arvo={yritys.YTunnus} />
          {yritys.RekisterointiPvm && moment(yritys.RekisterointiPvm).isValid() && (
            <NewInfoRow labelId="company-registry_date" arvo={moment(yritys.RekisterointiPvm).format('DD.MM.YYYY')} />
          )}
          {Array.isArray(officesDetailArray) && officesDetailArray.length > 0 && (
            <NewInfoRow labelId="company-offices" arvo={officesDetailArray} />
          )}
          {Array.isArray(responsibleDetailArray) && responsibleDetailArray.length > 0 && (
            <NewInfoRow labelId="company-responsible" arvo={responsibleDetailArray} />
          )}
          {Array.isArray(legalPersonDetailArray) && legalPersonDetailArray.length > 0 && (
            <NewInfoRow labelId="company-legal_persons" arvo={legalPersonDetailArray} />
          )}
          <NewInfoRow labelId="sanctions-title" arvo={sanctionDetailArray} />
        </Container>
        <div className="email-address-new">
          <p className="body-lg-bold">
            <FormattedMessage id="company-contact-new" defaultMessage="Onko tiedoissa virhe?" />
          </p>
          <p style={{marginBottom: '0px'}}>
            <FormattedMessage id="company-contact-email" defaultMessage="Ota yhteyttä: " />
            <a
              href={`mailto:${contactEmail}`}
              className="link-sm"
              style={{color: 'var(--colors-tummansininen, #00008e)'}}
            >
              {contactEmail}
            </a>
          </p>
          <FormattedMessage
            id="company-changed_info"
            defaultMessage="Muuttuneet tiedot voit ilmoittaa muutosilmoituslomakkeella osoitteeseen "
          />
          <a
            href={`mailto:${recordOfficeEmail}`}
            className="link-sm"
            style={{color: 'var(--colors-tummansininen, #00008e)'}}
          >
            {recordOfficeEmail}.
          </a>
          <p>
            <a href={companyChangeFormUrl} className="link-sm" style={{color: 'var(--colors-tummansininen, #00008e)', marginRight: '3px'}}>
              <FormattedMessage id="company-change-form-link" />
            </a>
            <FormattedMessage id="company-change-form-end" />
          </p>
        </div>
      </section>
    </>
  );
};

export default NewCompany;
